import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth, useClerk } from "@clerk/react";
import { fetchEntitlementStatus } from "./entitlementApi";

const EntitlementContext = createContext(null);
const ENTITLEMENT_CACHE_KEY = "studio_entitlement_status";

const readCachedEntitlement = (userId) => {
  try {
    const cached = JSON.parse(
      window.sessionStorage.getItem(ENTITLEMENT_CACHE_KEY) || "null"
    );

    if (cached?.userId === userId && cached?.entitlement?.active) {
      return cached.entitlement;
    }
  } catch (_) {
    window.sessionStorage.removeItem(ENTITLEMENT_CACHE_KEY);
  }

  return null;
};

const writeCachedEntitlement = (userId, entitlement) => {
  if (!userId || !entitlement?.active) {
    window.sessionStorage.removeItem(ENTITLEMENT_CACHE_KEY);
    return;
  }

  window.sessionStorage.setItem(
    ENTITLEMENT_CACHE_KEY,
    JSON.stringify({ userId, entitlement })
  );
};

export const EntitlementProvider = ({ children }) => {
  const { isLoaded, isSignedIn, getToken, userId } = useAuth();
  const { signOut } = useClerk();
  const [state, setState] = useState({
    status: "loading",
    entitlement: null,
    error: "",
  });

  const loadEntitlement = useCallback(async () => {
    if (!isLoaded) {
      setState((current) => ({ ...current, status: "loading" }));
      return null;
    }

    if (!isSignedIn) {
      window.sessionStorage.removeItem(ENTITLEMENT_CACHE_KEY);
      setState({
        status: "signed_out",
        entitlement: null,
        error: "",
      });
      return null;
    }

    setState((current) => ({
      ...current,
      status: "loading",
      error: "",
    }));

    const result = await fetchEntitlementStatus(getToken);

    if (result.status === 200) {
      const entitlement = result.data?.entitlement || null;
      writeCachedEntitlement(userId, entitlement);
      setState({
        status: "ready",
        entitlement,
        error: "",
      });
      return entitlement;
    }

    if (result.status === 401) {
      window.sessionStorage.removeItem(ENTITLEMENT_CACHE_KEY);
      setState({
        status: "signed_out",
        entitlement: null,
        error: "",
      });
      await signOut();
      return null;
    }

    writeCachedEntitlement(userId, null);
    setState({
      status: "error",
      entitlement: null,
      error: result.data?.error || "Unable to load account access",
    });
    return null;
  }, [getToken, isLoaded, isSignedIn, signOut, userId]);

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      const cachedEntitlement = readCachedEntitlement(userId);

      if (cachedEntitlement) {
        setState((current) => ({
          ...current,
          entitlement: cachedEntitlement,
        }));
      }
    }

    loadEntitlement().catch((err) => {
      console.error("Entitlement load failed:", err);
      setState({
        status: "error",
        entitlement: null,
        error: "Unable to load account access",
      });
    });
  }, [loadEntitlement, userId]);

  const updateEntitlement = useCallback(
    (entitlement) => {
      writeCachedEntitlement(userId, entitlement);
      setState({
        status: "ready",
        entitlement: entitlement || null,
        error: "",
      });
    },
    [userId]
  );

  const value = useMemo(
    () => ({
      ...state,
      hasActiveEntitlement: Boolean(state.entitlement?.active),
      isPurchased: Boolean(state.entitlement?.active),
      isEntitlementLoading: state.status === "loading" && !state.entitlement?.active,
      isEntitlementResolved:
        state.status !== "loading" || Boolean(state.entitlement?.active),
      reloadEntitlement: loadEntitlement,
      updateEntitlement,
    }),
    [loadEntitlement, state, updateEntitlement]
  );

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
};

export const useEntitlement = () => {
  const context = useContext(EntitlementContext);

  if (!context) {
    throw new Error("useEntitlement must be used inside EntitlementProvider");
  }

  return context;
};
